import {
  Activity,
  ActivityApi,
  ActivityList,
  ErrorResponse,
  EventTypeQuery,
} from '@citruscamps/citrus-client'
import { Dispatch, useMemo, useState } from 'react'
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
  useInfiniteQuery,
  useQuery,
} from 'react-query'
import { DefaultDetailsPagination, UnlimitedPagination } from '../constants/pagination'
import { Pagination } from '../interfaces/pagination'
import { generateApiConfig } from '../utils/client-config'
import { generateListKey } from '../utils/key-generator'
import { useRequestHandler } from './useRequestHandler'
import { useLocalStorage } from './useLocalStorage'
import { useRouter } from './useRouter'
import { FilterDateRange } from '../interfaces/filter-range'

const DefaultFetchProps: ActivityFetchProps = {
  sort: 'created',
  order: 'DESC',
  filter: {},
}

export interface ActivityFilterProps {
  types?: EventTypeQuery[]
  user_id?: string
  member_id?: string
  contact_id?: string
  program_attendee_id?: string
  order_id?: string
  created?: FilterDateRange
}

export interface ActivityFetchProps {
  sort?: string
  order?: 'ASC' | 'DESC'
  filter: ActivityFilterProps
}

export interface ActivityPagination extends Pagination {
  access_limited?: boolean
}

interface IProps {
  programId?: string
  pagination?: ActivityPagination
  fetchProps?: ActivityFetchProps
  overwriteFilters?: boolean
}

interface FetchActivities {
  accessLimited: boolean
  data: Activity[]
  error: ErrorResponse | null
  fetchProps: ActivityFetchProps
  isError: boolean
  isLoading: boolean
  pagination: ActivityPagination
  setFetchProps: (value?: ActivityFetchProps) => void
  setPagination: Dispatch<ActivityPagination>
}

export const useFetchActivities = ({
  programId = '',
  pagination: initialPagination = DefaultDetailsPagination,
  overwriteFilters,
  ...props
}: IProps): FetchActivities => {
  const { requestHandler } = useRequestHandler()
  const { pathname } = useRouter()
  const initialFetchProps: ActivityFetchProps = props.fetchProps || DefaultFetchProps
  const [fetchProps, setFetchProps] = useLocalStorage<ActivityFetchProps>(
    `activity-filter-${pathname}`,
    initialFetchProps,
    overwriteFilters,
  )
  const [pagination, setPagination] = useState<ActivityPagination>(initialPagination)
  const queryKeys = generateListKey({
    type: 'activity',
    program_id: programId,
    pagination,
    sort: fetchProps.sort,
    order: fetchProps.order,
    query: fetchProps.filter,
  })

  const {
    isLoading,
    isError,
    data: resp,
    error,
  } = useQuery<ActivityList, ErrorResponse>(
    queryKeys,
    async ({ signal }) => {
      if (!programId) {
        throw new Error('Unable to fetch data')
      }
      const client = new ActivityApi(generateApiConfig())
      const skip = pagination.limit ? pagination.page * pagination.limit : undefined
      const response: ActivityList = await requestHandler<ActivityList>(() =>
        client.findProgramActivitys({
          program_id: programId,
          limit: pagination.limit,
          skip,
          order: fetchProps.order,
          sort: fetchProps.sort,
          type: fetchProps.filter?.types ? fetchProps.filter.types : undefined,
          user_id: fetchProps.filter?.user_id || undefined,
          member_id: fetchProps.filter?.member_id || undefined,
          contact_id: fetchProps.filter?.contact_id || undefined,
          program_attendee_id: fetchProps.filter?.program_attendee_id || undefined,
          order_id: fetchProps.filter?.order_id || undefined,
          created_lt: fetchProps.filter.created?.lt || undefined,
          created_gt: fetchProps.filter?.created?.gt || undefined,
          created_lte: fetchProps.filter.created?.lte || undefined,
          created_gte: fetchProps.filter?.created?.gte || undefined,
        }),
      )
      return response
    },
    { enabled: !!programId },
  )

  const _setFetchProps = (val?: ActivityFetchProps) => {
    setPagination(initialPagination)
    return setFetchProps(val || DefaultFetchProps)
  }

  return {
    accessLimited: !!resp?.access_limited,
    data: resp?.data || [],
    error,
    fetchProps,
    isError,
    isLoading,
    pagination: {
      ...pagination,
      count: resp?.count || pagination.count,
      total: resp?.total || pagination.total,
      page: resp?.page || pagination.page,
      page_count: resp?.page_count || pagination.page_count,
    },
    setFetchProps: _setFetchProps,
    setPagination,
  }
}

interface FetchInfiniteActivity {
  isAccessLimited: boolean
  data: Activity[]
  error: ErrorResponse | null
  fetchProps: ActivityFetchProps
  isError: boolean
  isFetching: boolean
  isFetchingNextPage: boolean
  isLoading: boolean
  hasNextPage: boolean
  pagination: ActivityPagination
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<InfiniteQueryObserverResult<ActivityList, ErrorResponse>>
  setFetchProps: (value?: ActivityFetchProps) => void
}

export const useFetchInfiniteActivities = ({
  programId = '',
  pagination = DefaultDetailsPagination,
  overwriteFilters,
  ...props
}: IProps): FetchInfiniteActivity => {
  const { requestHandler } = useRequestHandler()
  const { pathname } = useRouter()
  const initialFetchProps: ActivityFetchProps = props.fetchProps || DefaultFetchProps
  const [fetchProps, setFetchProps] = useLocalStorage<ActivityFetchProps>(
    `activity-filter-${pathname}`,
    initialFetchProps,
    overwriteFilters,
  )
  const queryKeys = generateListKey({
    type: 'activity',
    program_id: programId,
    pagination: UnlimitedPagination,
    sort: fetchProps.sort,
    order: fetchProps.order,
    query: fetchProps.filter,
  })
  const {
    data,
    error,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
    fetchNextPage,
    hasNextPage = false,
  } = useInfiniteQuery<ActivityList, ErrorResponse>(
    queryKeys,
    async ({ signal, pageParam: pg = pagination }) => {
      if (!programId) {
        throw new Error('Unable to fetch data')
      }
      const client = new ActivityApi(generateApiConfig())
      const skip = pg.limit ? pg.page * pg.limit : undefined
      const resp: ActivityList = await requestHandler<ActivityList>(() =>
        client.findProgramActivitys(
          {
            program_id: programId,
            limit: pagination.limit,
            skip,
            order: fetchProps.order,
            sort: fetchProps.sort,
            type:
              !!fetchProps.filter?.types && fetchProps.filter?.types.length > 0
                ? fetchProps.filter.types
                : undefined,
            user_id: fetchProps.filter?.user_id || undefined,
            member_id: fetchProps.filter?.member_id || undefined,
            contact_id: fetchProps.filter?.contact_id || undefined,
            program_attendee_id: fetchProps.filter?.program_attendee_id || undefined,
            order_id: fetchProps.filter?.order_id || undefined,
            created_lt: fetchProps.filter.created?.lt || undefined,
            created_gt: fetchProps.filter?.created?.gt || undefined,
            created_lte: fetchProps.filter.created?.lte || undefined,
            created_gte: fetchProps.filter?.created?.gte || undefined,
          },
          { signal },
        ),
      )
      return resp
    },
    {
      getNextPageParam: (resp: ActivityList): Pagination | undefined => {
        if (!resp.page_count || resp.page < resp.page_count - 1) {
          return {
            ...pagination,
            count: resp?.count,
            total: resp?.total,
            page: resp?.page + 1,
            page_count: resp?.page_count,
          }
        }
        return undefined
      },
      getPreviousPageParam: (resp: ActivityList): Pagination | undefined => {
        if (!resp.page_count || resp.page <= resp.page_count) {
          return {
            ...pagination,
            count: resp?.count,
            total: resp?.total,
            page: resp?.page - 1,
            page_count: resp?.page_count,
          }
        }
        return undefined
      },
      enabled: !!programId,
    },
  )

  const _setFetchProps = (val?: ActivityFetchProps) => {
    return setFetchProps(val || DefaultFetchProps)
  }
  const accessLimited = useMemo(
    () =>
      (data?.pages || []).reduce<boolean>(
        (isLimited: boolean, p) => isLimited || !!p.access_limited,
        false,
      ),
    [data],
  )
  const resp = [...(data?.pages || [])].pop()
  const _hasNextPage = hasNextPage && (!resp?.access_limited || resp.count > 0)
  return {
    isAccessLimited: accessLimited,
    data: (data?.pages || []).reduce<Activity[]>((list, p) => [...list, ...p.data], []) || [],
    error,
    fetchProps,
    hasNextPage: _hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
    pagination: {
      ...pagination,
      count: resp?.count || pagination.count,
      total: resp?.total || pagination.total,
      page: resp?.page || pagination.page,
      page_count: resp?.page_count || pagination.page_count,
      access_limited: resp?.access_limited,
    },
    fetchNextPage,
    setFetchProps: _setFetchProps,
  }
}
